import { createIsNotCompatibleHelper } from "@todesktop/client-util/checkIfCompatibleWithBackend.js";
import { invoke } from "./invoke.js";
import { createOnAndOffNamespaceListeners, isDev, } from "./utils.js";
/**
 * Methods for interactng with electron updater
 *
 * @remarks
 * This package exposes a number of methods that allow you to access properties and interact with methods on electron updater.
 *
 * @experimental
 * @public
 * @packageDocumentation
 */
const isNotCompatible = createIsNotCompatibleHelper("electronUpdater", "3.24.0");
/**
 * Asks the server whether there is an update.
 * @public
 */
export async function checkForUpdates() {
    if (isNotCompatible("checkForUpdates"))
        return;
    if (isNotAvailableInDev("checkForUpdates"))
        return;
    return invoke({
        namespace: "electronUpdater",
        method: "checkForUpdates",
        args: [],
    });
}
/**
 * Checks if there's an update and notifies the user.
 *
 * @param options - title: title of the notification; body: body of the notification;
 * @public
 */
export async function checkForUpdatesAndNotify(downloadNotification) {
    if (isNotCompatible("checkForUpdatesAndNotify"))
        return;
    if (isNotAvailableInDev("checkForUpdatesAndNotify"))
        return;
    return invoke({
        namespace: "electronUpdater",
        method: "checkForUpdatesAndNotify",
        args: downloadNotification ? [downloadNotification] : [],
    });
}
/**
 * Restarts the app and installs the update after it has been downloaded. It should only be called after update-downloaded has been emitted.
 *
 * @param options - isSilent: *windows-only* Runs the installer in silent mode. Defaults to `false`; isForceRunAfter: Run the app after finish even on silent install. Not applicable for macOS. Ignored if `isSilent` is set to `false`.
 * @public
 */
export async function quitAndInstall(options = {}) {
    if (isNotCompatible("quitAndInstall"))
        return;
    if (isNotAvailableInDev("quitAndInstall"))
        return;
    return invoke({
        namespace: "electronUpdater",
        method: "quitAndInstall",
        args: [options.isSilent, options.isForceRunAfter],
    });
}
/**
 * Checks if the auto updater is active.
 *
 * @public
 */
export async function isUpdaterActive() {
    if (isNotCompatible("isUpdaterActive"))
        return;
    if (isNotAvailableInDev("isUpdaterActive"))
        return;
    return invoke({
        namespace: "electronUpdater",
        method: "isUpdaterActive",
        args: [],
    });
}
/**
 * Start downloading update manually. You can use this method if `autoDownload` option is set to `false`.
 *
 * @public
 */
export async function downloadUpdate() {
    if (isNotCompatible("downloadUpdate"))
        return;
    if (isNotAvailableInDev("downloadUpdate"))
        return;
    return invoke({
        namespace: "electronUpdater",
        method: "downloadUpdate",
        args: [],
    });
}
/**
 * Whether to allow version downgrade (when a user from the beta channel wants to go back to the stable channel).
 *
 * Taken in account only if channel differs (pre-release version component in terms of semantic versioning).
 *
 * @public
 */
export async function getAllowDowngrade() {
    if (isNotCompatible("getAllowDowngrade"))
        return;
    return invoke({
        namespace: "electronUpdater",
        property: "allowDowngrade",
    });
}
/**
 * *GitHub provider only.* Get all release notes (from current version to latest), not just the latest.
 *
 * @public
 */
export async function getAllowPrerelease() {
    if (isNotCompatible("getAllowPrerelease"))
        return;
    return invoke({
        namespace: "electronUpdater",
        property: "allowPrerelease",
    });
}
/**
 * Whether to automatically download an update.
 *
 * @public
 */
export async function getAutoDownload() {
    if (isNotCompatible("getAutoDownload"))
        return;
    return invoke({
        namespace: "electronUpdater",
        property: "autoDownload",
    });
}
/**
 * Whether to automatically install a downloaded update on app quit (if `quitAndInstall` was not called before).
 *
 * @public
 */
export async function getAutoInstallOnAppQuit() {
    if (isNotCompatible("getAutoInstallOnAppQuit"))
        return;
    return invoke({
        namespace: "electronUpdater",
        property: "autoInstallOnAppQuit",
    });
}
/**
 * Get the update channel. Not applicable for GitHub. Doesn't return `channel` from the update configuration, only if was previously set.
 *
 * @public
 */
export async function getChannel() {
    if (isNotCompatible("getChannel"))
        return;
    return invoke({
        namespace: "electronUpdater",
        property: "channel",
    });
}
const listeners = createOnAndOffNamespaceListeners("electronUpdater");
/**
 * Subcribes to an electronUpdater event.
 *
 * @param eventName - The event name to subscribe to.
 * @param callback - The callback function to execute when the event occurs.
 * @returns event id.
 * @public
 */
export const on = listeners.on;
/**
 * Unsubscribes from an electronUpdater event.
 *
 * @param options - eventName: The event name to unsubscribe from; callback: eventId: The eventId that was returned from the initial `electronUpdater.on` invocation.
 * @public
 */
export const off = listeners.off;
/**
 * Unsubscribes all electronUpdater listeners from a target event.
 *
 * @param options - eventName: The event name to unsubscribe from;
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
/**
 * Utils specific to this file
 */
const isNotAvailableInDev = (functionName) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(`Execution of ${functionName} skipped. Auto-updates are not available when running a local build.`);
        return true;
    }
    return false;
};
